import { css } from '@emotion/css';
import { useMemo, useState } from 'react';
import { useNavigator } from '../../routing/navigator';
import { QrIconBlue } from '../../shared/assets/icons';
import { useUser } from '../../shared/providers/user-provider';
import { Colors } from '../../shared/styles/colors';
import { SourceTypeEnum } from '../../shared/types/enums';
import {
  Pedestal,
  Socket,
  Source,
  SourceStatusEnum,
  Valve,
} from '../../shared/types/server-types';
import { SourceModal } from '../pedestal-page/source-modals/SourceModal';
import { ActivateMoreSocketCard } from './source-cards/ActivateMoreSocketCard';
import { ActivateMoreValveCard } from './source-cards/ActivateMoreValveCard';
import { h3Style, h4Style } from '../../shared/styles/text';
import { Motion, Radius, unstyledButton } from '../../shared/styles/tokens';

interface IActivateMoreProps {
  sessionSourceType: SourceTypeEnum | undefined;
  pedestal: Pedestal;
}

export const ActivateMore = (props: IActivateMoreProps): JSX.Element => {
  const navigator = useNavigator();
  const { sessionSourceType, pedestal } = props;
  const [modalVisible, setModalIsVisible] = useState<boolean>(false);
  const user = useUser();

  const availableSources = () => {
    if (sessionSourceType === SourceTypeEnum.SOCKET) {
      const availableValve = pedestal.valves.find(
        (valve) => valve.status === SourceStatusEnum.AVAILABLE,
      );
      return availableValve as Source;
    } else {
      const availableSocket = pedestal.sockets.find(
        (socket) => socket.status === SourceStatusEnum.AVAILABLE,
      );
      const availablePluggedSocket = pedestal.sockets.find(
        (socket) => socket.status === SourceStatusEnum.PLUGGED_IN,
      );
      return (availablePluggedSocket as Source) ?? (availableSocket as Source);
    }
  };

  const activeModalSource = useMemo(
    () => availableSources(),
    [sessionSourceType, pedestal],
  );

  return (
    <div className={activateMoreStyle}>
      <h3 className={h3Style}>Activate more</h3>
      <div className={activateMoreContainerStyle}>
        {activeModalSource &&
          activeModalSource.type === SourceTypeEnum.SOCKET && (
            <ActivateMoreSocketCard
              setModalVisible={setModalIsVisible}
              socket={activeModalSource as Socket}
              pedestal={pedestal}
            />
          )}
        {activeModalSource &&
          activeModalSource.type === SourceTypeEnum.VALVE && (
            <ActivateMoreValveCard
              setModalVisible={setModalIsVisible}
              valve={activeModalSource as Valve}
              pedestal={pedestal}
            />
          )}
        <button
          type="button"
          id="scanCard"
          className={activateBoxStyle}
          onClick={navigator.toScan}
        >
          <div>
            <h4 className={h4Style}>Scan</h4>
            <span>another pedestal</span>
          </div>
          <div>
            <QrIconBlue />
          </div>
        </button>
      </div>
      <SourceModal
        isVisible={modalVisible}
        setIsVisible={setModalIsVisible}
        user={user!}
        pedestal={pedestal}
        source={activeModalSource}
      />
    </div>
  );
};

const activateMoreStyle = css`
  width: 100%;
  margin-top: 0.5rem;

  > h3 {
    margin-bottom: 0.75rem;
  }
`;

const activateMoreContainerStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const activateBoxStyle = css`
  ${unstyledButton}
  min-height: 4.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  border: 2px solid ${Colors.CAPRI_BLUE};
  border-radius: ${Radius.md};
  background: ${Colors.WHITE};
  transition:
    background-color ${Motion.fast},
    transform ${Motion.fast},
    box-shadow ${Motion.fast};

  h4 {
    color: ${Colors.CAPRI_BLUE};
  }

  span {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.75rem;
    line-height: 100%;
    color: ${Colors.TEXT_SECONDARY};
  }

  > div:last-child {
    flex: 0 0 auto;
    display: flex;
  }

  @media (hover: hover) {
    &:hover {
      background: ${Colors.CAPRI_BLUE_TINT};
      box-shadow: 0 6px 16px -10px rgba(16, 42, 67, 0.35);
    }
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: 2px;
  }
`;
